import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@mui/material';

import ImportTransactions, { type ImportTransactionsRef } from './ImportTransactions';
import UploadDocuments, { type UploadDocumentsRef } from './UploadDocuments';
import ReimbursementOverview, { type ReimbursementOverviewRef } from './Reimbursement';
import { type ConnectStatementsRef } from './ConnectStatements';
import {
    ConnectStatementsFooterSpacer,
    createConnectStatementsContent,
    createConnectStatementsFooter,
} from './ConnectStatements/stepConfig';
import shouldShowConnectStatementsWizardStep, {
    isConnectStatementsResolved,
    isStatementUploadResolved,
} from './ConnectStatements/visibility';
import CompletionPage from './Completion';
import RedesignButtonTheme from './components/RedesignButtonTheme';
import AccountingSetupIntro, {
    IntroBackButton,
    IntroContinueButton,
    INTRO_FROM_PARAM,
    INTRO_NEXT_PARAM,
} from './AccountingSetupIntro';
import SetupStepNavigator, { type SetupNavigatorStep } from './components/SetupStepNavigator';
import useSetupVersionIdentify from './hooks/useSetupVersionIdentify';

import {
    WaiverToastsProvider,
    useWaiverToastRegistry,
} from 'modules/Onboarding/AccountingSetup/StatementUpload/WaiverToasts';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import StepWizard2, {
    type SidebarHelpers,
    type StepConfig2,
    type StepFooterHelpers,
    type StepHelpers,
} from 'modules/common/components/StepWizard/StepWizard2';
import {
    useAccountingOnboardingProgress,
    type OnboardingProgress,
} from 'services/bookkeeping/queries/useAccountingOnboardingProgress';
import useUpdateOnboardingStep from 'services/bookkeeping/mutations/useUpdateOnboardingStep';
import { useMember } from 'modules/common/queries/member';
import { isSCorpMember } from 'utils/businessStructure';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import IMC from 'modules/IMC';
import useAutoStatementsEligibility from 'modules/Onboarding/AccountingSetup/ConnectStatements/hooks/useAutoStatementsEligibility';
import useAutoStatementsSkip from 'modules/Onboarding/AccountingSetup/ConnectStatements/hooks/useAutoStatementsSkip';
import {
    ACCOUNTING_SETUP_PROGRESS_PATH,
    STATEMENTS_INTRO_PATH,
} from 'modules/Onboarding/AccountingSetup/Banking/constants';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';

// Helper function to check if there are personal accounts from bank connection
export function hasPersonalAccounts(onboardingProgress: OnboardingProgress | undefined): boolean {
    return (
        (onboardingProgress?.bank_connection_data?.confirmed_personal_accounts &&
            onboardingProgress.bank_connection_data.confirmed_personal_accounts.length > 0) ||
        false
    );
}

// Helper function to determine if import transactions step should be shown
export function shouldShowImportTransactionsStep(onboardingProgress: OnboardingProgress | undefined): boolean {
    // Show if there are currently personal accounts (before completion)
    if (hasPersonalAccounts(onboardingProgress)) {
        return true;
    }

    // Show if the transaction import step has been completed or skipped (after completion)
    const transactionImportStatus = onboardingProgress?.transaction_import_status;
    if (transactionImportStatus === 'completed') {
        return true;
    }

    // Don't show if no personal accounts and step hasn't been processed yet
    return false;
}

export interface AccountingSetupCompletionContext {
    shouldShowImportTransactions: boolean;
    isSCorp: boolean;
    shouldShowConnectStatements: boolean;
    /**
     * V1 redesign (FeatureGates.AccountingSetupRedesignV1): statements belong to business accounts
     * setup, so accounting setup must be able to complete without a statements status at all —
     * not merely without the step. Defaults to false so the legacy contract is unchanged.
     */
    isRedesignV1?: boolean;
}

// Whether every applicable step has reached a terminal state (completed, or explicitly skipped for
// steps that weren't shown). Drives the AccountingSetupPage redirect to the completion route — a step
// left out of this list is a step that redirect ignores.
export function isAccountingSetupComplete(
    onboardingProgress: OnboardingProgress | undefined,
    {
        shouldShowImportTransactions,
        isSCorp,
        shouldShowConnectStatements,
        isRedesignV1 = false,
    }: AccountingSetupCompletionContext
): boolean {
    if (!onboardingProgress) {
        return false;
    }

    // Always required: Upload Documents
    const uploadDocumentsCompleted = onboardingProgress.other_document_upload_status === 'completed';

    // Conditionally required: Import Transactions (only if step was shown)
    const importTransactionsCompleted = shouldShowImportTransactions
        ? onboardingProgress.transaction_import_status === 'completed'
        : onboardingProgress.transaction_import_status === 'skipped'; // Must be explicitly skipped if not shown

    // Conditionally required: Home Office Setup (only for S-Corp members)
    const homeOfficeSetupCompleted = isSCorp
        ? onboardingProgress.home_office_setup_status === 'completed'
        : onboardingProgress.home_office_setup_status === 'skipped'; // Must be explicitly skipped for non-S-Corp

    // Conditionally required: Connect Statements (only if step was shown).
    //
    // Under V1 this wizard doesn't own the field at all, so it can't demand anything of it — not even
    // 'skipped'. Business accounts setup drives it to a terminal state on its own schedule, and a
    // member who finished accounting setup first would otherwise sit on /completion forever.
    const connectStatementsCompleted =
        isRedesignV1 ||
        (shouldShowConnectStatements
            ? onboardingProgress.connect_automated_statements_status === 'completed'
            : onboardingProgress.connect_automated_statements_status === 'skipped'); // Must be explicitly skipped if not shown

    return (
        uploadDocumentsCompleted &&
        importTransactionsCompleted &&
        homeOfficeSetupCompleted &&
        connectStatementsCompleted
    );
}

// The wizard's step routes. Single source for the `steps` memo, the direct-URL redirect effects and
// STEP_STATUS_FIELD_BY_PATH below — keeping those in sync by hand is how AP-2496 comes back, since a
// rename in only one of them empties candidatePaths and no test notices.
export const ACCOUNTING_SETUP_INTRO_PATH = '/onboarding/accounting-setup/intro';
export const IMPORT_TRANSACTIONS_PATH = '/onboarding/accounting-setup/import-transactions';
export const REIMBURSEMENT_OVERVIEW_PATH = '/onboarding/accounting-setup/reimbursement-overview';
export const CONNECT_STATEMENTS_PATH = '/onboarding/accounting-setup/connect-statements';
export const UPLOAD_DOCUMENTS_PATH = '/onboarding/accounting-setup/upload-documents';
export const ACCOUNTING_SETUP_COMPLETION_PATH = '/onboarding/accounting-setup/completion';

// The onboarding-progress field each wizard step reports its own status through. Keyed by the step's
// route so a step that isn't registered for this member can't be picked as a redirect target.
const STEP_STATUS_FIELD_BY_PATH: Record<string, keyof OnboardingProgress> = {
    [IMPORT_TRANSACTIONS_PATH]: 'transaction_import_status',
    [REIMBURSEMENT_OVERVIEW_PATH]: 'home_office_setup_status',
    [CONNECT_STATEMENTS_PATH]: 'connect_automated_statements_status',
    [UPLOAD_DOCUMENTS_PATH]: 'other_document_upload_status',
};

// Under the V1 redesign the statements step belongs to business accounts setup, so this wizard stops
// claiming connect_automated_statements_status — dropping it here is what keeps a member who has yet
// to sync statements from being routed back into a step the accounting wizard no longer registers.
export function getStepStatusFieldByPath(isRedesignV1: boolean): Record<string, keyof OnboardingProgress> {
    if (!isRedesignV1) {
        return STEP_STATUS_FIELD_BY_PATH;
    }

    return Object.fromEntries(
        Object.entries(STEP_STATUS_FIELD_BY_PATH).filter(([path]) => path !== CONNECT_STATEMENTS_PATH)
    ) as Record<string, keyof OnboardingProgress>;
}

// The V1 in-wizard navigator, in wizard order. A row is shown only when its route is registered for
// this member, so a non-S-Corp member never sees "Claim reimbursements".
//
// None revisitable, because no step's footer walks back into the one before it: Claim reimbursements
// hides Back on its questionnaire, and Submit documents hides it in phase 1. The second is deliberate —
// Claim reimbursements keeps its answers in local state only and creates accountable plans on
// confirm, so returning to it would wipe the form and confirming again duplicate every month's plan.
interface NavigatorStepConfig extends SetupNavigatorStep {
    /** The route whose status field marks the row done, named rather than read off `paths` by position. */
    statusPath: string;
}

const NAVIGATOR_STEPS: readonly NavigatorStepConfig[] = [
    { label: 'Review personal accounts', paths: [IMPORT_TRANSACTIONS_PATH], statusPath: IMPORT_TRANSACTIONS_PATH },
    {
        label: 'Claim reimbursements',
        paths: [REIMBURSEMENT_OVERVIEW_PATH],
        statusPath: REIMBURSEMENT_OVERVIEW_PATH,
    },
    { label: 'Submit documents', paths: [UPLOAD_DOCUMENTS_PATH], statusPath: UPLOAD_DOCUMENTS_PATH },
];

/** The accounting setup substep routes this member is registered for, in wizard order. */
export function getAccountingSubstepPaths(
    onboardingProgress: OnboardingProgress | undefined,
    isSCorp: boolean
): string[] {
    return [
        ...(shouldShowImportTransactionsStep(onboardingProgress) ? [IMPORT_TRANSACTIONS_PATH] : []),
        ...(isSCorp ? [REIMBURSEMENT_OVERVIEW_PATH] : []),
        UPLOAD_DOCUMENTS_PATH,
    ];
}

/**
 * The navigator rows this member walks. A row is done only once 'completed', which is what
 * isAccountingSetupComplete demands of a step the member is shown.
 */
export function getNavigatorSteps(
    registeredPaths: readonly string[],
    onboardingProgress: OnboardingProgress | undefined,
    stepStatusFieldByPath: Record<string, keyof OnboardingProgress>
): SetupNavigatorStep[] {
    return NAVIGATOR_STEPS.filter((step) => registeredPaths.includes(step.statusPath)).map(
        ({ statusPath, ...step }) => ({
            ...step,
            isComplete: onboardingProgress?.[stepStatusFieldByPath[statusPath]] === 'completed',
        })
    );
}

/** On a navigator step, with at least one other to go to: a lone step has nowhere to navigate. */
export function shouldShowNavigator(steps: readonly SetupNavigatorStep[], pathname: string): boolean {
    return steps.length >= 2 && steps.some((step) => step.paths.includes(pathname));
}

// Where to send a member who hit the completion route without finishing setup. Walks the registered
// steps in wizard order and returns the first one they still owe us, or undefined when they owe us
// nothing the wizard can show them.
//
// undefined is a real outcome, not only the empty-list case. isAccountingSetupComplete demands
// exactly 'skipped' from a step the member was never shown, and a hidden step can sit short of that
// permanently: transaction_import reaches 'in_progress' from Bank.tsx and AccountingOverview.tsx,
// while every auto-skip effect fires only from 'not_started'. Naming a finished step here would
// bounce the member between /completion and that step forever, so the caller renders the completion
// page instead of redirecting.
//
// Under V1 a listed step is owed until 'completed', matching the navigator. The legacy flow also
// accepts 'skipped', a real finish for its statements step.
export function getFirstIncompleteStepPath(
    stepPaths: readonly string[],
    onboardingProgress: OnboardingProgress | undefined,
    stepStatusFieldByPath: Record<string, keyof OnboardingProgress> = STEP_STATUS_FIELD_BY_PATH,
    isRedesignV1 = false
): string | undefined {
    const finishedStatuses = isRedesignV1 ? ['completed'] : ['completed', 'skipped'];
    return stepPaths
        .filter((path) => path in stepStatusFieldByPath)
        .find((path) => {
            return !finishedStatuses.includes(String(onboardingProgress?.[stepStatusFieldByPath[path]]));
        });
}

/**
 * Where a member standing on the old connect-statements route belongs, or null to stay on it.
 *
 * The route is not in the steps array whenever the step is hidden, and StepWizard2 404s an unlisted
 * path, so leave it. Forward into the banking wizard only while that wizard will still register the
 * path, which under V1 is exactly while the step is unresolved. Eligibility is not consulted there,
 * matching the banking wizard: a member with nothing to enable still walks in and ConnectStatements
 * records the skip. A completed status still reads as available under the legacy predicate, but the
 * banking wizard omits a settled step — landing there 404s, and its guard then drops the member on
 * the labelling step (connect-account under V1, classify-accounts before it), out of this flow.
 *   gate on  + unresolved      -> its new home
 *   gate on  + settled         -> past it, stay in this flow
 *   gate off + step hidden     -> past it, the long-standing behaviour
 */
function getConnectStatementsRedirectPath(
    onboardingProgress: OnboardingProgress,
    isRedesignV1: boolean,
    statementsStepAvailable: boolean
): string | null {
    if (isRedesignV1) {
        return isConnectStatementsResolved(onboardingProgress) ? UPLOAD_DOCUMENTS_PATH : STATEMENTS_INTRO_PATH;
    }

    return statementsStepAvailable ? null : UPLOAD_DOCUMENTS_PATH;
}

/** The intro, routed on to `stepPath` on Continue and back to `fromPath` on Back. */
export function getIntroPathFor(stepPath: string, fromPath?: string): string {
    const params = new URLSearchParams({ [INTRO_NEXT_PARAM]: stepPath });
    if (fromPath) {
        params.set(INTRO_FROM_PARAM, fromPath);
    }
    return `${ACCOUNTING_SETUP_INTRO_PATH}?${params}`;
}

/**
 * Whether the member has entered any substep. Starting one is recorded on entry, past the intro, so
 * backing out of the intro doesn't count.
 */
function hasStartedSubstep(onboardingProgress: OnboardingProgress | undefined, isSCorp: boolean): boolean {
    const statusFieldByPath = getStepStatusFieldByPath(true);
    return getAccountingSubstepPaths(onboardingProgress, isSCorp).some(
        (path) => (onboardingProgress?.[statusFieldByPath[path]] ?? 'not_started') !== 'not_started'
    );
}

/** Where starting a substep from outside the wizard lands: the intro, until any substep is started. */
export function getSubstepEntryPath(
    stepPath: string,
    onboardingProgress: OnboardingProgress | undefined,
    isSCorp: boolean,
    fromPath?: string
): string {
    return hasStartedSubstep(onboardingProgress, isSCorp) ? stepPath : getIntroPathFor(stepPath, fromPath);
}

/** The update that marks a substep in progress, or null when it has none or is already completed. */
export function getSubstepStartUpdate(stepPath: string, onboardingProgress: OnboardingProgress | undefined) {
    const statusField = STEP_STATUS_FIELD_BY_PATH[stepPath];
    // Only a step not yet started moves to in_progress; rewriting `skipped` would block completion.
    if (!statusField || (onboardingProgress?.[statusField] ?? 'not_started') !== 'not_started') {
        return null;
    }
    return {
        step: statusField.replace(/_status$/, ''),
        status: 'in_progress',
        data: { started_at: new Date().toISOString() },
    };
}

function IntroFooterLeft() {
    return <IntroBackButton fallbackPath={ACCOUNTING_SETUP_PROGRESS_PATH} />;
}

function createIntroFooterRight(stepPaths: readonly string[], onStepStart: (stepPath: string) => void) {
    return function IntroFooterRight() {
        return <IntroContinueButton stepPaths={stepPaths} onStepStart={onStepStart} />;
    };
}

// Helper function to get button text
function getButtonText(isCompleting: boolean, isPhase1: boolean): string {
    if (isCompleting) {
        return 'Completing...';
    }
    return isPhase1 ? 'Continue' : 'Complete';
}

// Custom Complete button for ImportTransactions step
function ImportTransactionsCompleteButton({
    canProceed,
    importTransactionsRef,
    canComplete,
}: StepFooterHelpers & {
    importTransactionsRef: React.RefObject<ImportTransactionsRef>;
    canComplete: boolean;
}) {
    const isEnabled = canProceed && canComplete;

    const handleClick = () => {
        if (importTransactionsRef.current) {
            importTransactionsRef.current.openConfirmationModal();
        }
    };

    return (
        <Button variant="contained" size="large" onClick={handleClick} disabled={!isEnabled}>
            Complete
        </Button>
    );
}

// Content components for each step
function ImportTransactionsContent({
    helpers,
    importTransactionsRef,
    onboardingProgress,
    onCanCompleteChange,
}: {
    helpers: StepHelpers;
    importTransactionsRef: React.RefObject<ImportTransactionsRef>;
    onboardingProgress: OnboardingProgress | undefined;
    onCanCompleteChange: (canComplete: boolean) => void;
}) {
    return (
        <ImportTransactions
            ref={importTransactionsRef}
            onboardingProgress={onboardingProgress}
            setCanProceed={helpers.setCanProceed}
            onCanCompleteChange={onCanCompleteChange}
            onNavigateToNext={helpers.onNext}
        />
    );
}

function UploadDocumentsContent({
    helpers,
    phaseRef,
    uploadDocumentsRef,
    includeStatements,
}: {
    helpers: StepHelpers;
    phaseRef: React.RefObject<{ isPhase1: boolean }>;
    uploadDocumentsRef: React.RefObject<UploadDocumentsRef>;
    includeStatements: boolean;
}) {
    // The registry is mounted here rather than inside the step so the step can consume it: the
    // step is what completes, and completion is what has to retire the statements section's
    // waiver toasts. See `WaiverToasts`. Mounted even when the checklist has no statements, since
    // the step's completion consumes it unconditionally; it simply holds no toasts then.
    const waiverToasts = useWaiverToastRegistry();

    return (
        <WaiverToastsProvider value={waiverToasts}>
            <UploadDocuments
                ref={uploadDocumentsRef}
                helpers={helpers}
                phaseRef={phaseRef}
                includeStatements={includeStatements}
            />
        </WaiverToastsProvider>
    );
}

function ReimbursementOverviewContent({
    helpers,
    reimbursementOverviewRef,
    onViewChange,
}: {
    helpers: StepHelpers;
    reimbursementOverviewRef: React.RefObject<ReimbursementOverviewRef>;
    onViewChange: (showQuestionnaire: boolean) => void;
}) {
    return (
        <ReimbursementOverview
            ref={reimbursementOverviewRef}
            setCanProceed={helpers.setCanProceed}
            onNext={helpers.onNext}
            onViewChange={onViewChange}
        />
    );
}

function createReimbursementOverviewBackButton(
    reimbursementOverviewRef: React.RefObject<ReimbursementOverviewRef>,
    showQuestionnaire: boolean
) {
    return function ReimbursementOverviewBackButton() {
        const handleBack = () => {
            if (reimbursementOverviewRef.current) {
                reimbursementOverviewRef.current.handleBack();
            }
        };

        // Hide back button in questionnaire view, show in results view
        if (showQuestionnaire) {
            return <div />; // Empty div to prevent fallback to defaultLeftElement
        }

        return (
            <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: '12px',
                    textTransform: 'none',
                }}>
                Back
            </Button>
        );
    };
}

// Owns the pending state for the reimbursement footer. A real component, mounted by the render prop
// below, because handleComplete awaits a write plus two refetches before anything moves: without a
// pending state the button sits unchanged for about a second, reads as frozen, and stays clickable
// long enough to submit the step twice.
function ReimbursementCompleteButton({
    canProceed,
    showQuestionnaire,
    onComplete,
}: {
    readonly canProceed: boolean;
    readonly showQuestionnaire: boolean;
    readonly onComplete: () => Promise<void>;
}) {
    const [isCompleting, setIsCompleting] = useState(false);

    return (
        <Button
            variant="contained"
            disabled={!canProceed || isCompleting}
            onClick={async () => {
                setIsCompleting(true);
                try {
                    await onComplete();
                } catch (error) {
                    // handleComplete catches its own write failures, so this is the unexpected case.
                    // Report it rather than letting it escape as an unhandled rejection; the finally
                    // is what keeps the button usable afterwards.
                    sendDatadogRumError(
                        'Reimbursement overview completion failed',
                        error instanceof Error ? error : new Error('Reimbursement completion failed'),
                        {
                            source: 'AccountingSetup',
                            action: 'ReimbursementCompleteButton.onClick',
                            context: 'reimbursement_overview_completion',
                        }
                    );
                } finally {
                    setIsCompleting(false);
                }
            }}>
            {getButtonText(isCompleting, showQuestionnaire)}
        </Button>
    );
}

// Exported for test: this must return an element rather than call hooks itself, and nothing about the
// step config can express that constraint.
export function createFooterRightElement(
    reimbursementOverviewRef: React.RefObject<ReimbursementOverviewRef>,
    showQuestionnaire: boolean
) {
    return function FooterRightElement({ canProceed }: { canProceed: boolean }) {
        // No hooks in here. StepWizard2 invokes footerRightElement as a plain function during its own
        // render (StepWizard2.tsx:1140), so a hook placed here joins *StepWizard2's* hook list — and
        // since the footer differs per step, its hook count then changes between steps and React
        // throws "Rendered fewer hooks than expected". State belongs in the mounted child instead.
        return (
            <ReimbursementCompleteButton
                canProceed={canProceed}
                showQuestionnaire={showQuestionnaire}
                onComplete={async () => {
                    await reimbursementOverviewRef.current?.handleComplete();
                }}
            />
        );
    };
}

// Wrapper functions for step content to avoid nested components
function createImportTransactionsContent(
    importTransactionsRef: React.RefObject<ImportTransactionsRef>,
    onboardingProgress: OnboardingProgress | undefined,
    onCanCompleteChange: (canComplete: boolean) => void
) {
    return function ImportTransactionsContentWrapper(helpers: StepHelpers) {
        return (
            <ImportTransactionsContent
                helpers={helpers}
                importTransactionsRef={importTransactionsRef}
                onboardingProgress={onboardingProgress}
                onCanCompleteChange={onCanCompleteChange}
            />
        );
    };
}

function createImportTransactionsFooter(
    importTransactionsRef: React.RefObject<ImportTransactionsRef>,
    canComplete: boolean
) {
    return function ImportTransactionsFooterWrapper(helpers: StepFooterHelpers) {
        return (
            <ImportTransactionsCompleteButton
                {...helpers}
                importTransactionsRef={importTransactionsRef}
                canComplete={canComplete}
            />
        );
    };
}

function createUploadDocumentsContent(
    uploadDocumentsRef: React.RefObject<UploadDocumentsRef>,
    uploadDocumentsPhaseRef: React.RefObject<{ isPhase1: boolean }>,
    includeStatements: boolean
) {
    return function UploadDocumentsContentWrapper(helpers: StepHelpers) {
        return (
            <UploadDocumentsContent
                helpers={helpers}
                phaseRef={uploadDocumentsPhaseRef}
                uploadDocumentsRef={uploadDocumentsRef}
                includeStatements={includeStatements}
            />
        );
    };
}

// Custom footer for UploadDocuments step to handle dynamic button text.
// Exported for its own test: the step owns the write state on a ref, so the polling below is the
// only thing that gets it onto the screen, and nothing else in this file renders this component.
export function UploadDocumentsFooter({
    canProceed,
    phaseRef,
    uploadDocumentsRef,
}: Omit<StepFooterHelpers, 'onNext'> & {
    phaseRef: React.RefObject<{ isPhase1: boolean }>;
    uploadDocumentsRef: React.RefObject<UploadDocumentsRef>;
}) {
    const [isPhase1, setIsPhase1] = useState(true);
    const [isCompleting, setIsCompleting] = useState(false);
    const [isAdvancing, setIsAdvancing] = useState(false);

    // The step owns all three values on refs, so nothing here re-renders when they change. Polled
    // together, and into state rather than read during render: a mutable ref read while rendering
    // gives React no reason to update, so the button's loading state only ever appeared because
    // this interval happened to call a neighbouring setState on the same tick.
    useEffect(() => {
        const syncFromRefs = () => {
            if (phaseRef.current) {
                setIsPhase1(phaseRef.current.isPhase1);
            }
            setIsCompleting(uploadDocumentsRef.current?.isCompleting ?? false);
            setIsAdvancing(uploadDocumentsRef.current?.isAdvancing ?? false);
        };

        // Initial update
        syncFromRefs();

        const interval = setInterval(syncFromRefs, 100);

        return () => clearInterval(interval);
    }, [phaseRef, uploadDocumentsRef]);

    // Both phases go through `handleContinue`, which knows which one it is in: Phase 1 advances,
    // Phase 2 completes the step. Awaited either way, so the button holds its loading state for as
    // long as the work actually takes.
    const handleClick = async () => {
        await uploadDocumentsRef.current?.handleContinue();
    };

    return (
        <Button
            variant="contained"
            onClick={handleClick}
            disabled={!canProceed || isCompleting || isAdvancing}
            sx={{
                px: 4,
                py: 1.5,
                height: '100%',
                borderRadius: '12px',
                textTransform: 'none',
            }}>
            {getButtonText(isCompleting, isPhase1)}
        </Button>
    );
}

// Custom Back button for UploadDocuments step to handle internal phase navigation
function UploadDocumentsBackButton({
    uploadDocumentsRef,
    phaseRef,
}: {
    uploadDocumentsRef: React.RefObject<UploadDocumentsRef>;
    phaseRef: React.RefObject<{ isPhase1: boolean }>;
}) {
    const handleClick = () => {
        if (uploadDocumentsRef.current) {
            uploadDocumentsRef.current.handleBack();
        }
    };

    // Hide back button in Phase 1 (questionnaire), show in Phase 2 (document upload)
    const isPhase1 = phaseRef?.current?.isPhase1 ?? true; // Default to Phase 1 if ref not available

    if (isPhase1) {
        return <div />; // Hide the button completely in Phase 1
    }

    return (
        <Button
            variant="outlined"
            onClick={handleClick}
            sx={{
                px: 4,
                py: 1.5,
                borderRadius: '12px',
                textTransform: 'none',
            }}>
            Back
        </Button>
    );
}

function createUploadDocumentsBackButton(
    uploadDocumentsRef: React.RefObject<UploadDocumentsRef>,
    uploadDocumentsPhaseRef: React.RefObject<{ isPhase1: boolean }>
) {
    return function UploadDocumentsBackButtonWrapper() {
        return <UploadDocumentsBackButton uploadDocumentsRef={uploadDocumentsRef} phaseRef={uploadDocumentsPhaseRef} />;
    };
}

function createUploadDocumentsFooter(
    uploadDocumentsRef: React.RefObject<UploadDocumentsRef>,
    uploadDocumentsPhaseRef: React.RefObject<{ isPhase1: boolean }>
) {
    return function UploadDocumentsFooterWrapper(helpers: StepFooterHelpers) {
        return (
            <UploadDocumentsFooter
                {...helpers}
                phaseRef={uploadDocumentsPhaseRef}
                uploadDocumentsRef={uploadDocumentsRef}
            />
        );
    };
}

function createReimbursementOverviewContent(
    reimbursementOverviewRef: React.RefObject<ReimbursementOverviewRef>,
    onViewChange: (showQuestionnaire: boolean) => void
) {
    return function ReimbursementOverviewContentWrapper(helpers: StepHelpers) {
        return (
            <ReimbursementOverviewContent
                helpers={helpers}
                reimbursementOverviewRef={reimbursementOverviewRef}
                onViewChange={onViewChange}
            />
        );
    };
}

interface UnfinishedSetupCompletionRouteProps {
    stepPaths: string[];
    onboardingProgress: OnboardingProgress | undefined;
    isRedesignV1: boolean;
}

/** The completion route reached before setup is done: back to the first step still owed. */
function UnfinishedSetupCompletionRoute({
    stepPaths,
    onboardingProgress,
    isRedesignV1,
}: Readonly<UnfinishedSetupCompletionRouteProps>) {
    const firstIncompleteStepPath = getFirstIncompleteStepPath(
        stepPaths,
        onboardingProgress,
        getStepStatusFieldByPath(isRedesignV1),
        isRedesignV1
    );

    if (firstIncompleteStepPath) {
        return <Redirect to={firstIncompleteStepPath} />;
    }

    // Nothing outstanding that the wizard can show, even though isAccountingSetupComplete still
    // says no — a step the member never saw is stuck short of 'skipped'. Render the completion
    // page rather than redirecting back onto a step they have already finished, which would loop
    // on this guard forever with no wizard chrome mounted to close out of.
    return <CompletionPage />;
}

/**
 * AccountingSetup Page - StepWizard2 Implementation
 *
 * This uses StepWizard2 for consistent navigation and layout:
 * - All steps use the same header, footer, and navigation
 * - Built-in state management and validation
 * - Consistent user experience across all steps
 */
function AccountingSetupPage() {
    const isRedesignV1 = useStatsigGate(FeatureGates.AccountingSetupRedesignV1);
    useSetupVersionIdentify();

    const location = useLocation();
    const history = useHistory();
    const { sendTrackingEvent } = useEventTracking();

    // ✅ Move ALL hooks to top level before any conditional returns
    const { data: onboardingProgress, isLoading: isOnboardingProgressLoading } = useAccountingOnboardingProgress({
        enabled: true, // Override feature flag check for accounting setup flow
    });
    const { data: member, isLoading: isMemberLoading } = useMember();
    const updateStep = useUpdateOnboardingStep();

    const importTransactionsRef = useRef<ImportTransactionsRef>(null);
    const uploadDocumentsRef = useRef<UploadDocumentsRef>(null);
    const uploadDocumentsPhaseRef = useRef<{ isPhase1: boolean }>({ isPhase1: true });
    const reimbursementOverviewRef = useRef<ReimbursementOverviewRef>(null);
    const connectStatementsRef = useRef<ConnectStatementsRef>(null);
    const hasAttemptedSkip = useRef<boolean>(false);
    const hasAttemptedHomeOfficeSkip = useRef<boolean>(false);
    const hasTrackedSetupStarted = useRef(false);

    // Footers are called as plain functions, so the child state they show is lifted here.
    const [importCanComplete, setImportCanComplete] = useState(false);
    const [reimbursementShowsQuestionnaire, setReimbursementShowsQuestionnaire] = useState(true);

    // Drives whether the Connect Statements footer offers "I prefer to upload monthly" (hidden once a bank is enabled).
    const [connectStatementsCanSkip, setConnectStatementsCanSkip] = useState(true);

    // Check if import transactions step should be shown
    const isSCorp = isSCorpMember(member?.usertype || '');
    const substepPaths = useMemo(
        () => getAccountingSubstepPaths(onboardingProgress, isSCorp),
        [onboardingProgress, isSCorp]
    );
    const shouldShowImportTransactions = substepPaths.includes(IMPORT_TRANSACTIONS_PATH);

    const { mutate: updateStepStatus } = updateStep;
    const markSubstepStarted = useCallback(
        (stepPath: string) => {
            const update = getSubstepStartUpdate(stepPath, onboardingProgress);
            if (update) {
                updateStepStatus(update);
            }
        },
        [onboardingProgress, updateStepStatus]
    );

    // Auto Statements eligibility, read from the resolvability cache warmed during account classification.
    const { getEligibility } = useAutoStatementsEligibility();
    const autoStatementsEligibility = getEligibility(
        onboardingProgress?.bank_connection_data?.confirmed_business_accounts ?? []
    );
    const statementsStepAvailable = shouldShowConnectStatementsWizardStep(
        onboardingProgress,
        autoStatementsEligibility
    );
    // Under V1 the step belongs to business accounts setup, so this wizard never registers it —
    // regardless of eligibility or what the member has already done with it.
    const shouldShowConnectStatements = !isRedesignV1 && statementsStepAvailable;

    // Under V1 statements belong to business accounts setup, and completing statement_upload there is
    // what creates the member task for the rest. A member who finished that flow before it asked, or
    // walked it with statement verification off, never settled statement_upload, so they are still
    // asked here.
    const shouldAskForStatements = !isRedesignV1 || !isStatementUploadResolved(onboardingProgress);

    useAutoStatementsSkip({
        enabled: !isRedesignV1,
        onboardingProgress,
        eligibility: autoStatementsEligibility,
    });

    // Track accounting setup started -- once per mount, after the data its properties read has loaded.
    // Keyed on `onboardingProgress` alone it re-fired on every refetch of that query.
    useEffect(() => {
        if (hasTrackedSetupStarted.current || isOnboardingProgressLoading || isMemberLoading) {
            return;
        }

        hasTrackedSetupStarted.current = true;
        sendTrackingEvent('Accounting Setup Started', {
            category: 'accounting_setup',
            action: 'setup_started',
            userType: member?.usertype,
            hasPersonalAccounts: hasPersonalAccounts(onboardingProgress),
            stepName: 'accounting_setup',
        });
    }, [sendTrackingEvent, member?.usertype, onboardingProgress, isOnboardingProgressLoading, isMemberLoading]);

    // Auto-skip transaction import step if no personal accounts
    useEffect(() => {
        if (
            onboardingProgress &&
            !shouldShowImportTransactions &&
            onboardingProgress.transaction_import_status === 'not_started' && // Only auto-skip if not started
            !hasAttemptedSkip.current // Only attempt once per session
        ) {
            hasAttemptedSkip.current = true;

            // Skip the transaction import step in the backend
            updateStep.mutate({
                step: 'transaction_import',
                status: 'skipped',
                data: { reason: 'no_personal_accounts' },
            });
        }
    }, [onboardingProgress, shouldShowImportTransactions, updateStep]);

    // Auto-skip home office setup step for non-S-Corp members
    useEffect(() => {
        if (
            onboardingProgress &&
            member && // Unknown until loaded; skipping then would skip an S-Corp member's step
            !isSCorp &&
            onboardingProgress.home_office_setup_status === 'not_started' && // Only auto-skip if not started
            !hasAttemptedHomeOfficeSkip.current // Only attempt once per session
        ) {
            // Without this guard the effect re-fires on every render: each success invalidates progress,
            // cancelling the in-flight refetch, so the cache never sees 'skipped' and the loop sustains.
            hasAttemptedHomeOfficeSkip.current = true;

            // Skip the home office setup step in the backend for non-S-Corp members
            updateStep.mutate({
                step: 'home_office_setup',
                status: 'skipped',
                data: { reason: 'not_s_corp_member' },
            });
        }
    }, [onboardingProgress, member, isSCorp, updateStep]);

    // Handle URL redirect if user tries to access import-transactions when it should be hidden
    useEffect(() => {
        if (onboardingProgress && !shouldShowImportTransactions && location.pathname === IMPORT_TRANSACTIONS_PATH) {
            // Redirect to the first available step (home office setup)
            history.replace(REIMBURSEMENT_OVERVIEW_PATH);
        }
    }, [onboardingProgress, shouldShowImportTransactions, location.pathname, history]);

    // Create steps array dynamically based on whether personal accounts exist
    const steps: StepConfig2[] = useMemo(() => {
        const allSteps: StepConfig2[] = [];

        // Conditionally add Import Transactions step
        if (substepPaths.includes(IMPORT_TRANSACTIONS_PATH)) {
            allSteps.push({
                path: IMPORT_TRANSACTIONS_PATH,
                headerTitle: 'Import other business transactions',
                content: createImportTransactionsContent(
                    importTransactionsRef,
                    onboardingProgress,
                    setImportCanComplete
                ),
                validateOnMount: false, // Will be controlled by setCanProceed
                // No Back into the intro, which would skip this step's exit handler.
                footerLeftElement: ConnectStatementsFooterSpacer,
                footerRightElement: createImportTransactionsFooter(importTransactionsRef, importCanComplete),
                onStepExit: async () => {
                    // Call the ImportTransactions handleStepExit function
                    if (importTransactionsRef.current) {
                        return importTransactionsRef.current.handleStepExit();
                    }
                    return true; // Allow navigation if ref is not available
                },
            });
        }

        // Conditionally add Home Office Setup step for S-Corp members only
        if (substepPaths.includes(REIMBURSEMENT_OVERVIEW_PATH)) {
            allSteps.push({
                path: REIMBURSEMENT_OVERVIEW_PATH,
                content: createReimbursementOverviewContent(
                    reimbursementOverviewRef,
                    setReimbursementShowsQuestionnaire
                ),
                validateOnMount: false, // Will be controlled by setCanProceed
                footerLeftElement: createReimbursementOverviewBackButton(
                    reimbursementOverviewRef,
                    reimbursementShowsQuestionnaire
                ),
                footerRightElement: createFooterRightElement(reimbursementOverviewRef, reimbursementShowsQuestionnaire),
            });
        }

        // Conditionally add the Auto Statements step (flag on, not skipped, at least one eligible institution)
        if (shouldShowConnectStatements) {
            allSteps.push({
                path: CONNECT_STATEMENTS_PATH,
                content: createConnectStatementsContent(connectStatementsRef, {
                    onCanSkipChange: setConnectStatementsCanSkip,
                }),
                validateOnMount: false, // Continue is controlled by setCanProceed
                footerLeftElement: ConnectStatementsFooterSpacer,
                footerRightElement: createConnectStatementsFooter(connectStatementsRef, connectStatementsCanSkip),
                onStepExit: async () => {
                    if (connectStatementsRef.current) {
                        return connectStatementsRef.current.handleStepExit();
                    }
                    return true; // Allow navigation if ref is not available
                },
            });
        }

        // Always add Upload Documents step
        allSteps.push({
            path: UPLOAD_DOCUMENTS_PATH,
            content: createUploadDocumentsContent(uploadDocumentsRef, uploadDocumentsPhaseRef, shouldAskForStatements),
            footerLeftElement: createUploadDocumentsBackButton(uploadDocumentsRef, uploadDocumentsPhaseRef),
            footerRightElement: createUploadDocumentsFooter(uploadDocumentsRef, uploadDocumentsPhaseRef),
            validateOnMount: false, // Will be controlled by setCanProceed
            onStepExit: async () => {
                if (uploadDocumentsRef.current) {
                    return uploadDocumentsRef.current.handleStepExit();
                }
                return true; // Allow navigation if ref is not available
            },
        });

        if (isRedesignV1) {
            allSteps.unshift({
                path: ACCOUNTING_SETUP_INTRO_PATH,
                content: AccountingSetupIntro,
                footerLeftElement: IntroFooterLeft,
                footerRightElement: createIntroFooterRight(
                    allSteps.map((step) => step.path),
                    markSubstepStarted
                ),
            });
        }

        // Registered as the step after upload-documents so the wizard's own linear progression can
        // reach it: UploadDocuments finishes by calling helpers.onNext(), and StepWizard2 only
        // pushes /completion because it is processedSteps[currentStepIndex + 1]. Drop it and
        // upload-documents becomes isLastStep, so that same onNext() runs onComplete() instead and
        // the member is sent to the dashboard without ever seeing this page.
        //
        // Its content never renders: the completion route is intercepted below, standalone when
        // setup is finished and as a redirect when it isn't. CompletionPage is full-screen
        // (100vh, overflow hidden, absolutely centered children) and renders blank inside the
        // wizard's clipped content box, so this must stay a navigation target only. See AP-2496.
        allSteps.push({
            path: ACCOUNTING_SETUP_COMPLETION_PATH,
            content: CompletionPage,
            hideDefaultFooter: true, // No footer for completion page
        });

        return allSteps;
    }, [
        substepPaths,
        importTransactionsRef,
        onboardingProgress,
        uploadDocumentsRef,
        uploadDocumentsPhaseRef,
        importCanComplete,
        reimbursementShowsQuestionnaire,
        shouldShowConnectStatements,
        connectStatementsCanSkip,
        shouldAskForStatements,
        isRedesignV1,
        markSubstepStarted,
    ]);

    // Handle completion route - render standalone completion page without StepWizard2
    // Check if all applicable steps are completed (accounting for conditional steps)
    const isAllStepsCompleted = useMemo(
        () =>
            isAccountingSetupComplete(onboardingProgress, {
                shouldShowImportTransactions,
                isSCorp,
                shouldShowConnectStatements,
                isRedesignV1,
            }),
        [onboardingProgress, shouldShowImportTransactions, isSCorp, shouldShowConnectStatements, isRedesignV1]
    );

    const navigatorSteps = useMemo(
        () =>
            getNavigatorSteps(
                steps.map((step) => step.path),
                onboardingProgress,
                getStepStatusFieldByPath(isRedesignV1)
            ),
        [steps, onboardingProgress, isRedesignV1]
    );
    const showNavigator = isRedesignV1 && shouldShowNavigator(navigatorSteps, location.pathname);
    const isIntro = location.pathname === ACCOUNTING_SETUP_INTRO_PATH;

    const renderNavigator = useCallback(
        ({ isProcessing, goBackTo }: SidebarHelpers) => (
            <SetupStepNavigator
                steps={navigatorSteps}
                activePath={location.pathname}
                onStepClick={goBackTo}
                disabled={isProcessing}
            />
        ),
        [navigatorSteps, location.pathname]
    );

    // Show loading state while data is being fetched
    if (isOnboardingProgressLoading || isMemberLoading) {
        return <div>Loading...</div>; // You can replace this with a proper loading component
    }

    if (isAllStepsCompleted) {
        // Force redirect to completion route to avoid StepWizard2 interference
        if (location.pathname !== ACCOUNTING_SETUP_COMPLETION_PATH) {
            history.replace(ACCOUNTING_SETUP_COMPLETION_PATH);
            return null; // Prevent rendering while redirecting
        }

        return <CompletionPage />;
    }

    // Decided during render, not in an effect: StepWizard2 answers an unlisted path with a redirect to
    // /not-found as it mounts, which lands before any effect here could move the member on. Waits for
    // onboardingProgress, since shouldShowConnectStatementsWizardStep(undefined, …) answers true and
    // deciding without it would forward a member who has already skipped.
    if (location.pathname === CONNECT_STATEMENTS_PATH && onboardingProgress) {
        const connectStatementsRedirectPath = getConnectStatementsRedirectPath(
            onboardingProgress,
            isRedesignV1,
            statementsStepAvailable
        );

        if (connectStatementsRedirectPath) {
            return <Redirect to={connectStatementsRedirectPath} />;
        }
    }

    // Setup is unfinished, so the completion page has nothing to report yet — send the member to the
    // step they still owe us. Without this the route falls through to StepWizard2, which renders the
    // full-screen CompletionPage inside its clipped content box and shows the member a blank screen.
    // Intercepted here rather than from the step's content so the wizard chrome never mounts.
    if (location.pathname === ACCOUNTING_SETUP_COMPLETION_PATH) {
        return (
            <UnfinishedSetupCompletionRoute
                stepPaths={steps.map((step) => step.path)}
                onboardingProgress={onboardingProgress}
                isRedesignV1={isRedesignV1}
            />
        );
    }

    return (
        <RedesignButtonTheme enabled={isRedesignV1}>
            <StepWizard2
                onClose={() => {
                    // Track accounting setup abandoned
                    sendTrackingEvent('Accounting Setup Abandoned', {
                        category: 'accounting_setup',
                        action: 'setup_abandoned',
                        userType: member?.usertype,
                        currentStep: location.pathname,
                    });
                    history.push('/bookkeeping/dashboard');
                }}
                steps={steps}
                sidebarPlacement="overlay"
                sidebar={showNavigator ? renderNavigator : undefined}
                title="Accounting setup"
                onComplete={async () => {
                    // Track accounting setup completed
                    sendTrackingEvent('Accounting Setup Completed', {
                        category: 'accounting_setup',
                        action: 'setup_completed',
                        userType: member?.usertype,
                        hasPersonalAccounts: hasPersonalAccounts(onboardingProgress),
                        completionTime: new Date().toISOString(),
                    });

                    // Mark home office setup as completed when user finishes the reimbursement overview
                    if (isSCorp) {
                        try {
                            await updateStep.mutateAsync({
                                step: 'home_office_setup',
                                status: 'completed',
                                data: { completed_at: new Date().toISOString() },
                            });
                        } catch (error) {
                            sendDatadogRumError(
                                'Failed to mark home office setup as completed',
                                error instanceof Error
                                    ? error
                                    : new Error('Failed to mark home office setup as completed'),
                                {
                                    source: 'AccountingSetup',
                                    action: 'handleComplete',
                                    context: 'home_office_setup_completion',
                                    memberId: member?.id,
                                }
                            );
                        }
                    }
                    history.push('/bookkeeping/dashboard');
                }}
                includeIMC
                slotProps={{
                    header: {
                        rightElement: <IMC includePortal={false} disableReferralButton />,
                    },
                    content: {
                        sx: {
                            py: 0, // Remove vertical padding only
                            px: isIntro ? 0 : { xs: 3, sm: 4 },
                            paddingTop: '0', // Override MuiDialogContent-root padding-top
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            '& > div:first-child': {
                                width: '100%',
                                // The intro's split panel spans the full width.
                                maxWidth: isIntro ? '100%' : '800px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isIntro ? 'stretch' : 'center',
                            },
                        },
                    },
                }}
            />
        </RedesignButtonTheme>
    );
}

export default AccountingSetupPage;
